import { useEffect, useRef, useState } from "react";
import { LoaderCircle, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import type { Icd10Code } from "@/lib/queries";
import { useAiIcd10Search } from "@/lib/mutations";
import { useDropdownNav } from "@/hooks/useDropdownNav";

const MIN_QUERY = 2;
const DEBOUNCE_MS = 500;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the picked catalog code. The parent (Icd10Box) merges it
   * into the visit's codes (dedupe + toast) — immediate commit via the
   * visit page's notes autosave.
   */
  onPick: (code: Icd10Code) => void;
}

/**
 * Manual ICD-10 entry — searchable catalog picker (2026-11-03).
 *
 * Opened from the Icd10Box main dialog's "Manual entry" button (stacked on
 * top of it). Typing a diagnosis in clinical/everyday terms OR a partial/
 * complete ICD-10 code triggers a debounced POST /ai/icd10-search (AI
 * catalog lookup — no local ICD table exists). Selecting a match calls
 * onPick and STAYS OPEN (query reset, focus back in the search box) so
 * several codes can be added in a row; the parent toasts + dedupes.
 *
 * All search state lives in the SearchInner child, which Radix unmounts
 * with the DialogContent on close — so a reopen starts fresh without any
 * reset-in-effect. `pending` is a LOCAL flag (not the mutation's isPending):
 * it flips true only when the debounce fires, so the 500ms pre-call window
 * shows the neutral "type to search" hint instead of flashing "no matches".
 * The `seqRef` sequence guard drops responses that land after a newer
 * search or after a pick.
 *
 * Keyboard: ArrowUp/Down + Enter via useDropdownNav (same behaviour as every
 * other search dropdown in the app — e.g. the investigations search).
 */
export function ManualIcd10SearchDialog({ open, onOpenChange, onPick }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-maroon-700 dark:text-amber-500">Manual ICD-10 entry</DialogTitle>
          <DialogDescription>
            Search the ICD-10-CM catalog — type a diagnosis (e.g. &ldquo;migraine&rdquo;) or a code (e.g. &ldquo;G43&rdquo;). Select a match to add it to the visit&rsquo;s codes.
          </DialogDescription>
        </DialogHeader>
        {/* Keyed by open so a reopen remounts fresh even if Radix keeps the
            content mounted during the close animation. */}
        {open && <ManualIcd10SearchInner onPick={onPick} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualIcd10SearchInner({ onPick }: { onPick: (code: Icd10Code) => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Icd10Code[]>([]);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const { mutateAsync } = useAiIcd10Search();
  const inputRef = useRef<HTMLInputElement>(null);
  // Monotonic request sequence — drops stale responses when the user retypes
  // while a search is in flight (overlapping calls resolve out of order).
  const seqRef = useRef(0);

  // Debounced AI catalog search (>= MIN_QUERY chars, DEBOUNCE_MS delay).
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) return;
    const seq = ++seqRef.current;
    const t = setTimeout(() => {
      setPending(true);
      void mutateAsync({ query: q })
        .then((res) => {
          if (seq !== seqRef.current) return; // superseded by a newer search
          setResults(res.codes);
          setMessage(res.message ?? null);
        })
        .catch(() => {
          if (seq !== seqRef.current) return; // superseded
          setResults([]); // failure toasted by the mutation's onError
        })
        .finally(() => {
          if (seq !== seqRef.current) return;
          setPending(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, mutateAsync]);

  const pick = (c: Icd10Code) => {
    onPick(c);
    seqRef.current++; // any in-flight search response is now stale
    setQuery("");
    setResults([]);
    setPending(false);
    setMessage(null);
    inputRef.current?.focus();
  };

  const { highlight, highlightRef, onKeyDown } = useDropdownNav({
    count: results.length,
    onSelect: (i) => { if (results[i]) pick(results[i]); },
    inputRef,
  });

  return (
    <div className="space-y-2 py-1">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input
          ref={inputRef}
          className="pl-8"
          placeholder="Diagnosis or ICD-10 code…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus
        />
      </div>

      <div className="visible-scrollbar max-h-64 overflow-y-auto rounded-md border border-cream-200 bg-cream-100 dark:border-dark-700 dark:bg-dark-800">
        {pending && (
          <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-muted">
            <LoaderCircle size={14} className="animate-spin" /> Searching the ICD-10 catalog…
          </div>
        )}
        {!pending && query.trim().length < MIN_QUERY && (
          <div className="px-3 py-6 text-center text-sm text-muted">
            Type at least {MIN_QUERY} characters to search.
          </div>
        )}
        {!pending && query.trim().length >= MIN_QUERY && results.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-muted">
            {message ?? "No matching ICD-10 codes found — try another term."}
          </div>
        )}
        {!pending && results.length > 0 && (
          <ul className="p-1">
            {results.map((c, idx) => (
              <li key={c.code}>
                {/* onMouseDown (not onClick) + preventDefault — keeps the
                    input's blur/focus flow intact (investigations-search
                    pattern from VisitColumns). */}
                <button
                  type="button"
                  ref={idx === highlight ? highlightRef : undefined}
                  className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                    idx === highlight ? "bg-cream-200 dark:bg-dark-700" : ""
                  }`}
                  onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                >
                  <span className="shrink-0 font-mono text-xs font-bold text-maroon-700 dark:text-amber-500">{c.code}</span>
                  <span className="min-w-0 flex-1 text-ink dark:text-cream-50">{c.description}</span>
                  <Plus size={14} className="shrink-0 text-muted" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}